/**
 * Concurrent upload pool with a module-level global concurrency cap.
 *
 * The `concurrency` argument is a per-call ceiling; `GLOBAL_CAP` is enforced
 * across all simultaneous callers. Two collector cards uploading at once
 * cannot exceed GLOBAL_CAP in-flight tasks combined (ADR-026).
 */

const GLOBAL_CAP = 2;

let inFlight = 0;
const waiters: Array<() => void> = [];

function acquire(): Promise<void> {
  if (inFlight < GLOBAL_CAP) {
    inFlight++;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    waiters.push(() => {
      inFlight++;
      resolve();
    });
  });
}

function release(): void {
  inFlight--;
  const next = waiters.shift();
  if (next) next();
}

/**
 * `failures` carries the thrown errors so the caller can tell the user WHY.
 * The pool used to `catch {}` and return a bare count, which meant a rejected
 * file type — the API's Dutch message, or the client-side pre-check — reached
 * the screen as nothing but "1 bestand niet geüpload".
 */
export async function uploadPool<T>(
  items: T[],
  fn: (item: T) => Promise<void>,
  concurrency: number,
): Promise<{ succeeded: number; failed: number; failures: unknown[] }> {
  if (items.length === 0) return { succeeded: 0, failed: 0, failures: [] };

  const callerCap = Math.max(1, Math.min(concurrency, GLOBAL_CAP, items.length));
  let succeeded = 0;
  let failed = 0;
  const failures: unknown[] = [];
  const queue = [...items];

  async function worker() {
    while (queue.length > 0) {
      const item = queue.shift()!;
      await acquire();
      try {
        await fn(item);
        succeeded++;
      } catch (err) {
        failed++;
        failures.push(err);
      } finally {
        release();
      }
    }
  }

  await Promise.all(Array.from({ length: callerCap }, () => worker()));
  return { succeeded, failed, failures };
}
